import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import appIcon from '../assets/icon.png';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';

const navItems = [
  { path: '/app', label: 'Calendar', icon: '📅' },
  { path: '/app/budgets', label: 'Budgets', icon: '📊' },
  { path: '/app/accounts', label: 'Accounts', icon: '🏦' },
  { path: '/app/insights', label: 'Insights', icon: '💡' },
  { path: '/app/debts', label: 'Debts', icon: '💳' },
  { path: '/app/savings', label: 'Savings', icon: '🎯' },
  { path: '/app/ledger', label: 'Ledger', icon: '📒' },
  { path: '/app/settings', label: 'Settings', icon: '⚙️' },
];

const AppShell = () => {
  const navigate = useNavigate();
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen, closeMobile } = useSidebar();
  const { theme } = useTheme();

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.surfaceBackground }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={closeMobile}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: theme.overlayBg,
            zIndex: 40,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          width: collapsed ? 64 : 220,
          backgroundColor: theme.backgroundColor,
          borderRight: `1px solid ${theme.borderColor}`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease, transform 0.2s ease',
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 50,
          transform: mobileOpen ? 'translateX(0)' : undefined,
        }}
        className="app-sidebar"
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? '1rem 0.5rem' : '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            borderBottom: `1px solid ${theme.borderColor}`,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <img
            src={appIcon}
            alt="Forecast"
            style={{
              width: 28,
              height: 28,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
            }}
          />
          {!collapsed && (
            <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>Forecast</span>
          )}
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              onClick={closeMobile}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '0.625rem',
                padding: collapsed ? '0.625rem 0' : '0.625rem 0.75rem',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#2563eb' : theme.textColor,
                backgroundColor: isActive ? (theme.mode === 'dark' ? '#1E3A5F' : '#eff6ff') : 'transparent',
                justifyContent: collapsed ? 'center' : 'flex-start',
                transition: 'background-color 0.15s ease',
              })}
            >
              <span style={{ fontSize: '1.125rem' }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom actions */}
        <div
          style={{
            padding: '0.75rem',
            borderTop: `1px solid ${theme.borderColor}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: 6,
              color: theme.tertiaryText,
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '0.5rem',
            }}
          >
            <span>{collapsed ? '→' : '←'}</span>
            {!collapsed && <span>Collapse</span>}
          </button>
          <button
            onClick={handleLogout}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: 6,
              color: theme.tertiaryText,
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '0.5rem',
            }}
          >
            <span>🚪</span>
            {!collapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        style={{
          flex: 1,
          marginLeft: collapsed ? 64 : 220,
          transition: 'margin-left 0.2s ease',
          minHeight: '100vh',
        }}
        className="app-main"
      >
        {/* Mobile header */}
        <div className="app-mobile-header">
          <button
            onClick={() => setMobileOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
            }}
            aria-label="Menu"
          >
            <div style={{ width: 22, height: 2, backgroundColor: '#333', marginBottom: 5, borderRadius: 1 }} />
            <div style={{ width: 22, height: 2, backgroundColor: '#333', marginBottom: 5, borderRadius: 1 }} />
            <div style={{ width: 22, height: 2, backgroundColor: '#333', borderRadius: 1 }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <img src={appIcon} alt="Forecast" style={{ width: 24, height: 24 }} />
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>Forecast</span>
          </div>
          <div style={{ width: 34 }} />
        </div>

        <Outlet />
      </main>

      {/* Responsive styles */}
      <style>{`
        .app-mobile-header {
          display: none;
        }
        @media (max-width: 768px) {
          .app-sidebar {
            transform: translateX(-100%) !important;
            width: 220px !important;
          }
          .app-sidebar[style*="translateX(0)"] {
            transform: translateX(0) !important;
          }
          .app-main {
            margin-left: 0 !important;
          }
          .app-mobile-header {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            background: #fff;
            border-bottom: 1px solid #e5e7eb;
            position: sticky;
            top: 0;
            z-index: 30;
          }
        }
      `}</style>
    </div>
  );
};

export default AppShell;
